import { lazy, Suspense, type ComponentType } from "react";
import type { Options } from "react-markdown";
import { ResourceCodeBlock } from "@/components/resources/ResourceCodeBlock";
import { ArticleImage } from "@/components/site/ArticleImage";

type LazyMarkdownProps = Omit<Options, "remarkPlugins">;

// react-markdown + remark-gfm (and its micromark/mdast dependency tree) are
// only needed by articles whose body is stored as Markdown rather than
// pre-rendered HTML (see `isHtmlBody` in resources.$slug.tsx). Loading them
// eagerly for every article wastes ~38 KiB of JS on HTML-body articles,
// which never execute the parser. Deferred here so that weight only ships
// when an article actually needs it.
const MarkdownWithGfm = lazy(async () => {
  const [
    { default: ReactMarkdown },
    { default: remarkGfm },
    { default: rehypeRaw },
    { default: rehypeSanitize, defaultSchema },
    { default: rehypeSlug },
  ] = await Promise.all([
    import("react-markdown"),
    import("remark-gfm"),
    import("rehype-raw"),
    import("rehype-sanitize"),
    import("rehype-slug"),
  ]);

  // Legacy imports embed inline HTML (anchor targets such as
  // `<a id="4-replatform"></a>`, occasional <br>/<sup>). Without rehype-raw
  // those tags leak into the page as literal text. rehype-sanitize runs right
  // after, so raw HTML is parsed but still scrubbed of scripts, event
  // handlers and javascript: URLs — no XSS regression.
  const schema = {
    ...defaultSchema,
    // Keep author-provided anchor IDs intact: the default schema prefixes them
    // with `user-content-`, which silently breaks every in-article TOC
    // fragment that points at a raw `<a id="...">` target.
    clobberPrefix: "",
    attributes: {
      ...defaultSchema.attributes,
      "*": [...(defaultSchema.attributes?.["*"] ?? []), "id", "className", "class"],
    },
  };


  const Component: ComponentType<LazyMarkdownProps> = ({ components, ...props }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, schema], rehypeSlug]}

      components={{
        pre: ResourceCodeBlock as any,
        // GFM tables: keep them inside a horizontally scrollable shell so wide
        // comparison tables never break the editorial column on mobile.
        table: ({ children, ...rest }: any) => (
          <div className="my-8 w-full overflow-x-auto rounded-xl border border-border">
            <table {...rest} className="m-0 w-full min-w-[36rem] border-0 text-left">
              {children}
            </table>
          </div>
        ),
        img: ArticleImage,
        ...components,
      }}
      {...props}
    />
  );

  return { default: Component };
});

export function LazyMarkdown(props: LazyMarkdownProps) {
  return (
    <Suspense fallback={null}>
      <MarkdownWithGfm {...props} />
    </Suspense>
  );
}
